import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import CreateMultiAutocomplete from '../CreateMultiAutocomplete';
import ApartmentForm from './ApartmentForm';
import Group from './Group';
import messages from './messages';
import Option from './Option';
import Tag from './Tag';

function ApartmentAutocomplete({
  addAllApartmentsId,
  apartmentFormStyle,
  areAllApartmentsAdded,
  disabled,
  hasAddAllApartmentsOption,
  hasFormItemKey,
  loading,
  newItemId,
  optionsData,
  scrollableTags,
}) {
  const { control, getValues, watch } = useFormContext();
  const { formatMessage } = useIntl();

  const options = useMemo(
    () => [
      ...(hasAddAllApartmentsOption
        ? [
            {
              id: addAllApartmentsId,
              label: formatMessage(messages.apartmentAutocompleteSelectAddAll),
            },
          ]
        : []),
      {
        id: newItemId,
        label: formatMessage(messages.ApartmentAutocompleteSelectNewItemLabel),
      },
      ...optionsData,
    ],
    [optionsData, hasAddAllApartmentsOption]
  );

  function getPlaceHolder() {
    return areAllApartmentsAdded
      ? formatMessage(messages.apartmentAutocompleteSelectPlaceholderAllAdded)
      : formatMessage(messages.apartmentAutocompleteSelectPlaceholder);
  }

  return (
    <CreateMultiAutocomplete
      addAllOptionsId={addAllApartmentsId}
      areAllOptionsAdded={areAllApartmentsAdded}
      disabled={disabled}
      loading={loading}
      createForm={watch(hasFormItemKey) && <ApartmentForm control={control} apartmentFormStyle={apartmentFormStyle} />}
      createFormVisibilityKey={hasFormItemKey}
      customOption={(renderProps, option) => (
        <Option
          key={option.id}
          hasAddAllOptions={hasAddAllApartmentsOption}
          addAllOptionsId={addAllApartmentsId}
          newItemId={newItemId}
          option={option}
          optionLabelKey="label"
          renderProps={renderProps}
        />
      )}
      customTag={(tagValue, getTagProps) => (
        <Tag
          addAllOptionsId={addAllApartmentsId}
          hasAddAllOptions={hasAddAllApartmentsOption}
          getTagProps={getTagProps}
          key={tagValue.id}
          newItemId={newItemId}
          tagValue={tagValue}
        />
      )}
      helperText={formatMessage(messages.apartmentAutocompleteHelperText)}
      label={formatMessage(messages.ApartmentAutocompleteSelectLabel)}
      name="apartment"
      newItemId={newItemId}
      optionGroupKey="floor"
      optionLabelKey="label"
      options={options}
      placeholder={getValues()?.apartment?.length ? '' : getPlaceHolder()}
      renderGroup={(data) => <Group group={data.group || ''} items={data.children} key={data.key} />}
      scrollableTags={scrollableTags}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: true,
              fn: ({ state }) => {
                const popperRect = state.elements.popper.getBoundingClientRect();
                const available = window.innerHeight - popperRect.top - 8;
                const clamped = Math.max(available, 100);
                const listbox = state.elements.popper.querySelector('.MuiAutocomplete-listbox');
                if (listbox) {
                  listbox.style.maxHeight = `${clamped}px`;
                  listbox.style.overflowY = 'auto';
                }
              },
              name: 'sizeToFit',
              phase: 'beforeWrite',
              requires: ['computeStyles'],
            },
          ],
          placement: 'bottom',
        },
      }}
    />
  );
}

ApartmentAutocomplete.propTypes = {
  addAllApartmentsId: PropTypes.string,
  apartmentFormStyle: PropTypes.shape({}),
  areAllApartmentsAdded: PropTypes.bool,
  disabled: PropTypes.bool,
  hasAddAllApartmentsOption: PropTypes.bool,
  hasFormItemKey: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  newItemId: PropTypes.string.isRequired,
  optionsData: PropTypes.arrayOf(
    PropTypes.shape({
      floor: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.string.isRequired,
    })
  ).isRequired,
  scrollableTags: PropTypes.bool,
};

export default ApartmentAutocomplete;
